import React, { useState, useEffect, useMemo } from "react";
import { handleGetRequest } from "../../../services/GetTemplate";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import remarksImage from "../../../assets/png/remarks.png";
import { useFormik } from "formik";
import { handlePostRequest } from "../../../services/PostTemplate";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import Loading from "../../../components/Loader";
import { InputTextarea } from "primereact/inputtextarea";
import classNames from "classnames";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import NavigationComponent from "../../../components/NavigationComponent";
import { Carousel } from "primereact/carousel";
import { Dialog } from "primereact/dialog";
import EditViewParts from "../../Parts/Components/EditViewParts";
import EditViewVariant from "../../VariantManagement/EditViewVariant";
import * as Yup from "yup";
import baseURL, { endPoints } from "../../../Config";

const ViewQuery = () => {
    const [statusDropdown, setStatusDropdown] = useState([]);
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const [loadingBtn, setLoadingBtn] = useState(false);
    const { id: quotation_id } = useParams();

    // Get the status filter and other parameters from location state
    const fromStatus = location.state?.fromStatus || "pending";
    const fromQueryFilter = location.state?.fromQueryFilter || "all";
    const fromPage = location.state?.fromPage || 1;
    const fromRecordCount = location.state?.fromRecordCount || 10;

    const [queryData, setQueryData] = useState("");
    const [clientPhoneNumber, setClientPhoneNumber] = useState("");
    const [recordId, setRecordId] = useState("");
    const [requestData, setRequestData] = useState("");
    const [detailsArray, setDetailsArray] = useState([]);
    const [response, setResponse] = useState(false);
    const [loading, setLoading] = useState(false);
    const [displayDialog, setDisplayDialog] = useState(false);

    const getStatusDropdown = async () => {
        const response = await dispatch(handleGetRequest(endPoints.getStatusDropdown, false));
        if (response?.statusCode === 200) {
            setStatusDropdown(response?.data);
        }
    };
    
    useEffect(() => {
        getStatusDropdown();
        // eslint-disable-next-line
    }, []);

    const getQuotationsyId = async () => {
        setLoading(true);
        const response = await dispatch(handleGetRequest(endPoints?.getQuotationById(quotation_id), false));
        setResponse(response?.data);
        setClientPhoneNumber(response?.data?.clientPhone);
        setDetailsArray(response?.data?.details);
        const keyData = response?.data;
        formik.setFieldValue("status", "");
        setQueryData(keyData?.clientQuery);
        setRequestData(keyData?.clientRequest);
        setLoading(false);
    };

    useEffect(() => {
        if (quotation_id) {
            getQuotationsyId();
        }
        // eslint-disable-next-line
    }, [quotation_id]);

    const validationSchema = Yup.object().shape({
        reply: Yup.string().matches(/^[^\s]+(\s[^\s]+)*$/, "Leading and trailing space is not allowed"),
        remarks: Yup.string().matches(/^[^\s]+(\s[^\s]+)*$/, "Leading and trailing space is not allowed"),
    });

    const normalizeStatusKey = (value = "") => value?.toString().toLowerCase().replace(/\s+/g, "");

    const statusLookup = useMemo(() => {
        return statusDropdown?.reduce((acc, item) => {
            const key = normalizeStatusKey(item?.name);
            if (key) {
                acc[key] = item;
            }
            return acc;
        }, {});
    }, [statusDropdown]);

    const currentStatusKey = useMemo(() => normalizeStatusKey(response?.quotationStatus), [response?.quotationStatus]);

    const allowedStatusKeys = useMemo(() => {
        const rules = {
            pending: ["onhold", "processed", "rejected"],
            onhold: ["approved", "processed", "rejected"],
            rejected: ["onhold", "approved", "processed"],
            approved: ["onhold", "processed", "rejected"],
            processed: ["ordercompleted"],
        };
        if (rules[currentStatusKey]) {
            return rules[currentStatusKey];
        }
        return Object.keys(statusLookup);
    }, [currentStatusKey, statusLookup]);

    const filteredStatusOptions = useMemo(() => {
        return allowedStatusKeys.map((key) => statusLookup[key]).filter(Boolean);
    }, [allowedStatusKeys, statusLookup]);

    const defaultStatusId = useMemo(() => {
        switch (currentStatusKey) {
            case "onhold":
                return statusLookup["onhold"]?.id;
            case "rejected":
                return statusLookup["rejected"]?.id;
            case "processed":
                return statusLookup["processed"]?.id;
            case "approved":
                return statusLookup["approved"]?.id;
            default:
                return undefined;
        }
    }, [currentStatusKey, statusLookup]);

    const isStatusDropdownDisabled = currentStatusKey === "ordercompleted";

    const formik = useFormik({
        validationSchema: validationSchema,
        initialValues: {
            remarks: "",
            reply: "",
            status: "",
        },

        onSubmit: async (data) => {
            setLoadingBtn(true);

            const resolvedStatusId = data?.status
                ? Number(data?.status)
                : defaultStatusId !== undefined && defaultStatusId !== null
                ? Number(defaultStatusId)
                : undefined;

            let newData = {
                remarks: data?.remarks && data?.remarks !== response?.remarks?.[0]?.remarks ? data?.remarks : undefined,
                reply: data?.reply ? data?.reply : undefined,
                statusId: currentStatusKey === "pending" && !data?.status ? undefined : resolvedStatusId,
                quotationId: Number(quotation_id),
            };
            const res = await dispatch(handlePostRequest(newData, endPoints.quotationReply, false, true));
            if (res.statusCode === 200) {
                getQuotationsyId();
                formik.setFieldValue("reply", "");
                // Navigate back with preserved state
                history.push({
                    pathname: "/querymanagement",
                    state: {
                        fromStatus: fromStatus,
                        fromQueryFilter: fromQueryFilter,
                        fromPage: fromPage,
                        fromRecordCount: fromRecordCount
                    }
                });
            }
            setLoadingBtn(false);
        },
    });
    
    // Function to navigate back with preserved state
    const goBack = () => {
        history.push({
            pathname: "/querymanagement",
            state: {
                fromStatus: fromStatus,
                fromQueryFilter: fromQueryFilter,
                fromPage: fromPage,
                fromRecordCount: fromRecordCount
            }
        });
    };

    const createdDateAndTimeBodyTemplate = (createdDate) => {
        const isoDate = createdDate;
        const date = new Date(isoDate);

        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        const yyyy = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");

        let ampm = "AM";
        if (hours >= 12) {
            ampm = "PM";
            hours -= 12;
        }
        if (hours === 0) {
            hours = 12;
        }

        const formattedDateTime = `${dd}/${mm}/${yyyy} ${hours}:${minutes} ${ampm}`;
        return <p className="text-sm">{formattedDateTime}</p>;
    };

    const responsiveOptions = [
        {
            breakpoint: "1024px",
            numVisible: 3,
            numScroll: 3,
        },
    ];

    const carouselVehicleObj = (detailsArray) => {
        return (
            <div className="p-field col-12 md:col-12">
                <div className="no-header query-table-main">
                    <div className="queryRow">
                        <h3>{detailsArray?.variantName}</h3>{" "}
                        <img
                            src={`${baseURL}/${detailsArray?.variantImg?.[0]?.path}`}
                            style={{ borderRadius: "10px" }}
                            width="120px"
                            height="90px"
                            alt="Variant Image"
                        />
                    </div>
                    <div className="queryRow">
                        <h6>Maker Name</h6>
                        <p>{detailsArray?.variantMakerName}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Model Name</h6>
                        <p>{detailsArray?.variantModelName}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Category</h6>
                        <p>{detailsArray?.variantCategoryName}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Year</h6>
                        <p>{detailsArray?.variantYear}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Power Type</h6>
                        <p>{detailsArray?.variantPowerType}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Transmission</h6>
                        <p>{detailsArray?.variantTransmission}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Features</h6>
                        <p className="ml-4">{detailsArray?.variantFeature}</p>
                    </div>
                    <div className="flex justify-content-center">
                        <Button
                            className="Save-Button"
                            label="View Details"
                            type="button"
                            onClick={() => {
                                handleDialog(detailsArray?.variantId);
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const carouselPartsObj = (detailsArray) => {
        return (
            <div className="p-field col-12 md:col-12">
                <div className="no-header query-table-main">
                    <div className="queryRow">
                        <h3>{detailsArray?.partName}</h3>
                        <img
                            src={`${baseURL}/${detailsArray?.partImg?.[0]?.path}`}
                            style={{ borderRadius: "10px" }}
                            width="120px"
                            height="90px"
                            alt="Part Image"
                        />
                    </div>
                    <div className="queryRow">
                        <h6 className="font-bold">Total Parts</h6>
                        <p className="text-wrap font-bold">{response?.details?.length}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Part Number</h6>
                        <p className="text-wrap">{detailsArray?.partId ? detailsArray?.partId : "-"}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Variant Maker Name</h6>
                        <p className="text-wrap">{detailsArray?.variantMakerName ? detailsArray?.variantMakerName : "-"}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Variant Model Name</h6>
                        <p>{detailsArray?.variantModelName ? detailsArray?.variantModelName : "-"}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Part Variant Name</h6>
                        <p>{detailsArray?.partVariantName ? detailsArray?.partVariantName : "-"}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Variant Year</h6>
                        <p className="text-wrap">{detailsArray?.variantYear ? detailsArray?.variantYear : "-"}</p>
                    </div>
                    <div className="queryRow">
                        <h6>Price</h6>
                        <p className="text-wrap">{detailsArray?.partCost ? detailsArray?.partCost : "-"}</p>
                    </div>
                    <div className="flex justify-content-center">
                        <Button
                            className="Save-Button"
                            label="View Details"
                            type="button"
                            onClick={() => {
                                handleDialog(detailsArray?.partId);
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const handleDialog = (partId) => {
        setRecordId(partId);
        setDisplayDialog(true);
    };

    const onHide = () => {
        setDisplayDialog(false);
    };

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    const headerText = "Details";
    const header = <span className="title-border">{headerText}</span>;

    const whatsAppClickHandler = () => {
        if (!response?.details || response.details.length === 0) {
            alert("No details available");
            return;
        }

        const messages = response.details
            .map((item, index) => {
                return `*Detail #${index + 1}:*  
        Model: ${item?.variantModelName || "N/A"}  
        Variant: ${item?.variantName || "N/A"}  
        Year: ${item?.variantYear || "N/A"}  
        Part Name: ${item?.partName || "N/A"}  
        `;
            })
            .join("\n\n");

        const text = `*Hi! Welcome to Car2Parts.*  
    We are here to assist you about your queries:  
    
    ${messages}
    
    Feel free to ask—our team will get back to you shortly!  
    Genuine Used Parts | Best Prices | Fast Shipping`;

        try {
            const encodedText = encodeURIComponent(text);

            if (!response?.clientPhone) {
                alert("Client phone number is missing");
                return;
            }

            const link = `https://wa.me/${clientPhoneNumber}?text=${encodedText}`;
            window.open(link, "_blank");
        } catch (error) {
            console.error("Failed to open WhatsApp link:", error);
            alert("Something went wrong. Please try again.");
        }
    };

    return (
        <>
            {loading ? (
                <Loading />
            ) : (
                <>
                    <Dialog resizable={false} header={header} visible={displayDialog} maskClassName="zindex8888" blockScroll style={{ width: "50vw" }} onHide={onHide} draggable={false}>
                        {response?.isVehicle ? (
                            <EditViewVariant onHide={onHide} isEditable={false} variantdataId={recordId} />
                        ) : (
                            <EditViewParts onHide={onHide} isEditable={false} partsdataId={recordId} />
                        )}
                    </Dialog>

                    <form onSubmit={formik.handleSubmit}>
                        <NavigationComponent 
                            title="View Query" 
                            showBackButton={true}
                            onBackClick={goBack}
                        />
                        <div className="p-fluid p-formgrid grid query-resp mb-5 m-0 card-ml-1 dark-bg">
                            <div className="col-12 md:col-6">
                                <div className="p-field col-12 md:col-12">
                                    <Carousel
                                        value={detailsArray}
                                        numVisible={1}
                                        numScroll={1}
                                        responsiveOptions={responsiveOptions}
                                        className="custom-carousel"
                                        circular={false}
                                        itemTemplate={response?.isVehicle ? carouselVehicleObj : carouselPartsObj}
                                    />
                                </div>
                            </div>
                            <div className="col-12 md:col-6">
                                <div className="p-field col-12 md:col-12">
                                    <div className="remarksSection ">
                                        <h3>Remarks</h3>
                                        <div className="remarksImage">
                                            <img src={remarksImage} alt="Image Alt Text" style={{ height: "100px", width: "120px" }} />
                                        </div>
                                    </div>
                                    <div className="">
                                        <div
                                            className="flex field col-12 md:col-10 mx-5 align-items-baseline"
                                            style={{ paddingLeft: "0", marginBottom: "0", paddingBottom: "0" }}
                                        >
                                            <label className="mb-0"> Client's Name: </label>
                                            <p className="text-900 ml-2 pt-0">{response?.clientName}</p>
                                        </div>
                                        <div
                                            className="flex field col-12 md:col-10 mx-5 align-items-baseline"
                                            style={{ paddingLeft: "0", marginBottom: "0", paddingBottom: "0" }}
                                        >
                                            <label className="mb-0"> Client's Email: </label>
                                            <p className="text-900 ml-2">{response?.clientEmail}</p>
                                        </div>
                                        <div
                                            className="flex field col-12 md:col-10 mx-5 align-items-baseline"
                                            style={{ paddingLeft: "0", marginBottom: "0", paddingBottom: "0" }}
                                        >
                                            <label className="mb-0"> Client's Phone Number: </label>
                                            <p className="text-900 ml-2">{response?.clientPhone}</p>
                                        </div>
                                        <div
                                            className="field col-12 md:col-10 mx-5"
                                            style={{
                                                paddingLeft: "0",
                                                marginBottom: "0",
                                                maxHeight: "150px",
                                                overflow: "auto",
                                                wordWrap: "break-word",
                                                whiteSpace: "normal",
                                            }}
                                        >
                                            <label> Client's Query: </label>
                                            <span className="Label__Required"></span>
                                            <p className="text-900 ">{queryData}</p>
                                        </div>
                                        <div
                                            className="field col-12 md:col-10 mx-5"
                                            style={{
                                                paddingLeft: "0",
                                                marginBottom: "0",
                                                maxHeight: "150px",
                                                overflow: "auto",
                                                wordWrap: "break-word",
                                                whiteSpace: "normal",
                                            }}
                                        >
                                            <label> Client's Message: </label>
                                            <span className="Label__Required"></span>
                                            <p className="text-900">{requestData}</p>
                                        </div>
                                        <div className="field col-12 md:col-6 mx-5" style={{ paddingLeft: "0", marginBottom: "0" }}>
                                            <label> Status </label>
                                            <span className="Label__Required"></span>
                                            <Dropdown
                                                className={classNames({ "p-invalid": isFormFieldValid("status") }, "Dropdown__Round")}
                                                id="status"
                                                name="status"
                                                options={filteredStatusOptions.length ? filteredStatusOptions : statusDropdown}
                                                optionValue="id"
                                                optionLabel="name"
                                                filter
                                                showClear={!isStatusDropdownDisabled && !!formik?.values?.status}
                                                filterBy="label"
                                                showFilterClear={true}
                                                resetFilterOnHide={true}
                                                onChange={(e) => {
                                                    formik?.setFieldValue("status", e.target.value ? e.target.value : "");
                                                }}
                                                value={formik.values.status}
                                                placeholder={
                                                    response?.quotationStatus
                                                        ? response.quotationStatus.charAt(0).toUpperCase() + response.quotationStatus.slice(1)
                                                        : ""
                                                }
                                                disabled={isStatusDropdownDisabled}
                                            />
                                            {getFormErrorMessage("status")}
                                        </div>
                                        <div className="field col-12 md:col-10 mx-5" style={{ paddingLeft: "0", marginBottom: "0" }}>
                                            <label> Notes </label>
                                            <span className="Label__Required"></span>
                                            <InputTextarea
                                                id="remarks"
                                                name="remarks"
                                                value={formik?.values?.remarks}
                                                onChange={formik.handleChange}
                                                placeholder="Enter Remarks...."
                                                maxLength={200}
                                                rows={3}
                                                cols={10}
                                                autoResize
                                                className={classNames({ "p-invalid": isFormFieldValid("remarks") }, "textAreaField")}
                                            />
                                            {getFormErrorMessage("remarks")}
                                        </div>
                                        <div className="field col-12 md:col-10 mx-5" style={{ paddingLeft: "0" }}>
                                            <label> Reply To Customer </label>
                                            <span className="Label__Required"></span>
                                            <InputTextarea
                                                id="reply"
                                                name="reply"
                                                value={formik?.values?.reply}
                                                onChange={formik.handleChange}
                                                placeholder="Enter Reply...."
                                                maxLength={200}
                                                rows={3}
                                                cols={10}
                                                autoResize
                                                className={classNames({ "p-invalid": isFormFieldValid("reply") }, "textAreaField")}
                                            />
                                            {getFormErrorMessage("reply")}
                                        </div>
                                    </div>
                                    <div className="col-12 d-flex flex-row align-center text-center mt-4 pb-2">
                                        <Button className="Cancel-Button" label="Cancel" type="button" onClick={goBack} />

                                        <Button
                                            className="Save-Button ml-2"
                                            type="submit"
                                            label="Email"
                                            loading={loadingBtn}
                                            disabled={!formik?.values?.remarks && !formik?.values?.reply}
                                        />
                                        <Button
                                            style={{ margin: " 5px 5px 0px 5px", textAlign: "center", flexDirection: "column" }}
                                            className="Cancel-Button"
                                            type="button"
                                            onClick={whatsAppClickHandler}
                                        >
                                            <i className="pi pi-whatsapp" style={{ color: "green", transform: "scale(1.5)" }}></i>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-ml-1 dark-bg p-5">
                            <div className="flex">
                                <div className="col-6 border-round card-content reply-history">
                                    <p className="text-900 font-semibold text-xl border-bottom-1">Replies History</p>
                                    <div className="content chat-container">
                                        {response?.message?.map((item, idx) => {
                                            return (
                                                <React.Fragment key={idx}>
                                                    {item?.clientReply && (
                                                        <div className="chat-bubble">
                                                            <div className="customer">
                                                                <p className="mb-2">{item?.clientReply}</p>
                                                                {createdDateAndTimeBodyTemplate(item?.createdDate)}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {item?.reply && (
                                                        <div className="chat-bubble">
                                                            <div className="my-reply">
                                                                <p className="mb-2">{item?.reply}</p>
                                                                {createdDateAndTimeBodyTemplate(item?.createdDate)}
                                                            </div>
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="col-6 border-round card-content">
                                    <p className="text-900 font-semibold text-xl border-bottom-1">Notes History</p>
                                    <div className="content">
                                        <ul>
                                            {response?.remarks?.map(
                                                (item, index) =>
                                                    item?.remarks && (
                                                        <li key={index} className="text-900 text-wrap mr-3 mb-3">
                                                            <span className="font-semibold">{createdDateAndTimeBodyTemplate(item?.createdDate)}</span>
                                                            {item?.remarks}
                                                        </li>
                                                    )
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </>
            )}
        </>
    );
};

export default ViewQuery;